export interface ReaderDiffResult {
  originalHtml: string;
  adjustedHtml: string;
}

interface MappedToken {
  value: string;
  node: Text;
  start: number;
  end: number;
}

const TOKEN_PATTERN =
  /[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*|[^\s\p{L}\p{N}]/gu;
const MAX_DIFF_CELLS = 4_000_000;

const parseBody = (html: string): HTMLElement =>
  new DOMParser().parseFromString(html, "text/html").body;

const collectTokens = (root: HTMLElement): MappedToken[] => {
  const tokens: MappedToken[] = [];
  const walker = root.ownerDocument.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
  );

  let node = walker.nextNode() as Text | null;
  while (node) {
    TOKEN_PATTERN.lastIndex = 0;
    let match = TOKEN_PATTERN.exec(node.data);
    while (match) {
      const start = match.index;
      tokens.push({
        value: match[0],
        node,
        start,
        end: start + match[0].length,
      });
      match = TOKEN_PATTERN.exec(node.data);
    }
    node = walker.nextNode() as Text | null;
  }

  return tokens;
};

const findMatches = (
  original: MappedToken[],
  adjusted: MappedToken[],
): { original: Set<number>; adjusted: Set<number> } | null => {
  const width = adjusted.length + 1;
  const cells = (original.length + 1) * width;
  if (cells > MAX_DIFF_CELLS) return null;

  const matrix = new Uint32Array(cells);
  for (
    let originalIndex = 1;
    originalIndex <= original.length;
    originalIndex += 1
  ) {
    for (
      let adjustedIndex = 1;
      adjustedIndex <= adjusted.length;
      adjustedIndex += 1
    ) {
      const index = originalIndex * width + adjustedIndex;
      if (
        original[originalIndex - 1].value ===
        adjusted[adjustedIndex - 1].value
      ) {
        matrix[index] =
          matrix[(originalIndex - 1) * width + adjustedIndex - 1] + 1;
      } else {
        matrix[index] = Math.max(
          matrix[(originalIndex - 1) * width + adjustedIndex],
          matrix[originalIndex * width + adjustedIndex - 1],
        );
      }
    }
  }

  const matchedOriginal = new Set<number>();
  const matchedAdjusted = new Set<number>();
  let originalIndex = original.length;
  let adjustedIndex = adjusted.length;

  while (originalIndex > 0 && adjustedIndex > 0) {
    if (
      original[originalIndex - 1].value ===
      adjusted[adjustedIndex - 1].value
    ) {
      matchedOriginal.add(originalIndex - 1);
      matchedAdjusted.add(adjustedIndex - 1);
      originalIndex -= 1;
      adjustedIndex -= 1;
    } else if (
      matrix[(originalIndex - 1) * width + adjustedIndex] >=
      matrix[originalIndex * width + adjustedIndex - 1]
    ) {
      originalIndex -= 1;
    } else {
      adjustedIndex -= 1;
    }
  }

  return { original: matchedOriginal, adjusted: matchedAdjusted };
};

const annotateUnmatchedTokens = (
  tokens: MappedToken[],
  matchedIndexes: Set<number>,
  kind: "removed" | "added",
): void => {
  const rangesByNode = new Map<Text, Array<{ start: number; end: number }>>();

  tokens.forEach((token, index) => {
    if (matchedIndexes.has(index)) return;
    const ranges = rangesByNode.get(token.node) || [];
    const previous = ranges[ranges.length - 1];
    const gap = previous
      ? token.node.data.slice(previous.end, token.start)
      : "";

    if (previous && gap.trim() === "") {
      previous.end = token.end;
    } else {
      ranges.push({ start: token.start, end: token.end });
    }
    rangesByNode.set(token.node, ranges);
  });

  rangesByNode.forEach((ranges, node) => {
    [...ranges].reverse().forEach(({ start, end }) => {
      const selected = node.splitText(start);
      selected.splitText(end - start);
      const wrapper = node.ownerDocument.createElement(
        kind === "removed" ? "del" : "ins",
      );
      wrapper.setAttribute(
        "aria-label",
        kind === "removed" ? "Removed text" : "Added text",
      );
      wrapper.setAttribute("data-reader-diff", kind);
      selected.parentNode?.replaceChild(wrapper, selected);
      wrapper.appendChild(selected);
    });
  });
};

export const buildReaderDiff = (
  originalHtml: string,
  adjustedHtml: string,
): ReaderDiffResult | null => {
  if (typeof DOMParser === "undefined") return null;

  const originalBody = parseBody(originalHtml);
  const adjustedBody = parseBody(adjustedHtml);
  const originalTokens = collectTokens(originalBody);
  const adjustedTokens = collectTokens(adjustedBody);
  const matches = findMatches(originalTokens, adjustedTokens);
  if (!matches) return null;

  annotateUnmatchedTokens(originalTokens, matches.original, "removed");
  annotateUnmatchedTokens(adjustedTokens, matches.adjusted, "added");

  return {
    originalHtml: originalBody.innerHTML,
    adjustedHtml: adjustedBody.innerHTML,
  };
};
